'use client';

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  // `min-h-11` keeps every button at or above the 44px touch target a phone needs.
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[var(--radius-control)] text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-55 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary: 'bg-brand text-white hover:bg-brand-hover shadow-[var(--shadow-card)]',
        secondary: 'bg-surface text-ink border border-border-strong hover:bg-surface-muted',
        ghost: 'text-ink-muted hover:bg-surface-muted hover:text-ink',
        danger: 'bg-danger text-white hover:brightness-95',
        positive: 'bg-positive text-white hover:brightness-95',
        link: 'text-brand underline-offset-4 hover:underline',
      },
      size: {
        sm: 'min-h-9 px-3 text-[13px] [&_svg]:size-4',
        md: 'min-h-11 px-4 [&_svg]:size-4',
        lg: 'min-h-12 px-6 text-base [&_svg]:size-5',
        icon: 'min-h-11 w-11 [&_svg]:size-5',
        'icon-sm': 'min-h-9 w-9 [&_svg]:size-4',
      },
      block: { true: 'w-full', false: '' },
    },
    defaultVariants: { variant: 'primary', size: 'md', block: false },
  },
);

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    /** Shows a spinner and blocks further clicks. */
    loading?: boolean;
    /** Announced while loading — screen readers get the reason, not just a spinner. */
    loadingLabel?: string;
  };

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, block, asChild = false, loading = false, loadingLabel, children, disabled, ...props },
  ref,
) {
  const Comp = asChild ? Slot : 'button';

  if (asChild) {
    return (
      <Comp className={cn(buttonVariants({ variant, size, block }), className)} ref={ref} {...props}>
        {children}
      </Comp>
    );
  }

  return (
    <button
      className={cn(buttonVariants({ variant, size, block }), className)}
      ref={ref}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? (
        <>
          <Loader2 className="animate-spin" aria-hidden="true" />
          <span>{loadingLabel ?? children}</span>
        </>
      ) : (
        children
      )}
    </button>
  );
});

export { buttonVariants };
